import { createSelector } from "reselect";
import moment from "moment";
import { AppState } from "../rootReducer";

export const getCoachingSession = (state: AppState) =>
  state.coaching.coachingSession || {};

export const getCoachingRequesting = (state: AppState) =>
  state.coaching.requesting;

export const getHasCoachingResources = createSelector(
  getCoachingSession,
  coachingSession => {
    return coachingSession.resources.length > 0;
  },
);

export const getCoachingSessionId = createSelector(
  getCoachingSession,
  coachingSession => {
    return coachingSession._id;
  },
);

export const getHasCoachingSession = createSelector(
  getCoachingSessionId,
  coachingSessionId => {
    return !!coachingSessionId;
  },
);

export const getSessions = (state: AppState) =>
  state.coaching.coachingSession.sessions;

export const getCoachingGoals = createSelector(
  getCoachingSession,
  coachingSession => coachingSession.goals,
);

export const getCoachingSessionResources = createSelector(
  getCoachingSession,
  coachingSession => {
    return coachingSession.resources;
  },
);

export const getIsCoachingFinished = createSelector(
  getCoachingSession,
  coachingSession => {
    return coachingSession.isFinished || false;
  },
);

export const getIsForbidden = createSelector(
  getCoachingSession,
  coachingSession => {
    return coachingSession.isForbidden || false;
  },
);

export const getEndCoachingMessage = createSelector(
  getCoachingSession,
  coachingSession => {
    return coachingSession.endCoachingMessage || "";
  },
);

export const getTotalSessionsDone = createSelector(getSessions, sessions =>
  sessions.reduce((prev, curr) => !!curr.isDone + prev, 0),
);

export const getCoachId = createSelector(
  getCoachingSession,
  coachingSession => coachingSession.coachId,
);

export const getCoachName = createSelector(
  getCoachingSession,
  coachingSession => coachingSession.coachName || "Sans nom",
);

export const getCoachPicture = createSelector(
  getCoachingSession,
  coachingSession =>
    coachingSession.coachPicture ||
    "https://encrypted-tbn0.gstatic.com/images?q=tbn%3AANd9GcQopl87rjgIYDWv2zsOB5zHr7n8-Scl-DM1ug&usqp=CAU",
);

export const getIsCoachingSessionForbidden = createSelector(
  getCoachingSession,
  coachingSession => coachingSession.isForbidden,
);

export const getCoachingDurationInMonths = createSelector(
  getSessions,
  sessions => {
    if (!sessions.length) return 1;

    const startingDate = sessions[0].nextDate;

    const endDate = sessions[sessions.length - 1].nextDate;

    const durationInMonths =
      moment(endDate).diff(moment(startingDate), "months") + 1;

    return durationInMonths;
  },
);

export const getTotalDoneSessions = createSelector(getSessions, sessions => {
  if (!sessions.length) return 1;

  const total = sessions.reduce((prev, curr) => {
    return prev + !!curr.isDone;
  }, 0);
  return total;
});

export const getTotalDoneActions = createSelector(
  getCoachingSession,
  coachingSession => coachingSession.totalDone,
);

export const getGoalsResult = createSelector(
  getCoachingGoals,
  coachingGoals => {
    const totalGoals = coachingGoals.length;
    const totalFullDone = coachingGoals.reduce((prev, curr) => {
      const isFullyDone = curr.doneSteps.length >= curr.goalNumber;

      return prev + isFullyDone;
    }, 0);

    return `${totalFullDone} / ${totalGoals}`;
  },
);

export const getAllCoachRecommendSteps = createSelector(
  getCoachingGoals,
  goals => {
    return goals.reduce((acc, curr) => {
      acc = [...acc, ...curr.recommendedSteps];
      return acc;
    }, []);
  },
);

export const getAllCoachRecommendSkills = createSelector(
  getCoachingGoals,
  goals => {
    return goals.reduce((acc, curr) => {
      if (curr.skill) {
        acc.push(curr.skill);
      }
      return acc;
    }, []);
  },
);

const getCoachingResources = (state: AppState) =>
  state.coaching.coachingResources;

export const getCoachingResourcesWithDetails = createSelector(
  [getCoachingSessionResources, getCoachingResources],
  (coachingSessionResources, coachingResources) => {
    return coachingSessionResources.map(resource => {
      const coachingResourceObject =
        coachingResources.find(el => el._id === resource.resourceId) || {};
      return {
        ...coachingResourceObject,
        rating: resource.rating || 0,
        totalViews: resource.totalViews || 0,
        lastViewDate: resource.lastViewDate || null,
      };
    });
  },
);
